import { useCacheStorageSync } from '@/shared/lib/hooks/use-cache-storage/use-cache-storage-sync';
import DynamicBreadcrumbs from '@/app/ui/dynamic-breadcrumbs';
import { GlobalDialogs } from '@/app/ui/global-dialogs';
import { AppLoading } from '@/shared/ui/app-loading';
import { useAuthSession } from '@/entities/user/session';
import { memo, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import {
  Link,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { LoginStatus } from '@/entities/user/user';
import { CHANGE_PASSWORD, HOME, LOGIN } from '@/shared/config/routes';
import { useCurrentRoute } from '@/app/lib/navigation/use-current-route';
import { useNavigationByRules } from '@/app/lib/navigation/use-navigation-by-rules';
import { useDataGridStorageCleanup } from '@/app/lib/navigation/use-datagrid-storage-cleanup';

import { useRouteAccessGuard } from '@/app/lib/navigation/use-route-access-guard';
import {
  AppShell,
  Box,
  Burger,
  Divider,
  Flex,
  Group,
  Stack,
  Title,
} from '@mantine/core';
import { useDisclosure, useHotkeys } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { sentryDsn } from '@/app/lib/sentry';
import cs from 'clsx';
import { AppSearch } from '@/widgets/app-search';
import { ExternalLinks } from '@/widgets/external-links';
import { ContextMenu } from '@/shared/ui/context-menu';
import { ServiceSelector } from '@/widgets/service-selector';
import { HINotifications } from '@/widgets/hi-notifications';
import { NotificationsLog } from '@/widgets/notification-log';
import { Navigation } from '@/widgets/navigation';
import { useQueryClient } from '@tanstack/react-query';
import {
  ApplicationPreparation,
  prepareApplication,
} from '@/features/reference-data-prefetch';
import { useLogout } from '@/features/authentication';

const App = memo(() => {
  useCacheStorageSync();
  useDataGridStorageCleanup();
  const { loginStatus, reauthenticating } = useAuthSession();
  const isAuth = loginStatus === LoginStatus.login;
  const queryClient = useQueryClient();
  const logout = useLogout(false);
  const location = useLocation();
  const navigate = useNavigate();

  useRouteAccessGuard();
  const lastPamirPathRef = useRef<string>('/');
  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true);

  const currentRoute = useCurrentRoute();
  const [navigation, isNavigationFetching, navigationError] =
    useNavigationByRules();

  const hidden =
    loginStatus === LoginStatus.unauthorized ||
    currentRoute?.show?.navigation === false;

  const noHeader = currentRoute?.noHeader === true;

  const selectedView = useMemo(() => {
    if (location.pathname.startsWith('/service/')) {
      return location.pathname.split('/')[2];
    }
    return 'pamir';
  }, [location.pathname]);

  const [loadedViews, setLoadedViews] = useState<Set<string>>(
    () => new Set(['pamir']),
  );

  useEffect(() => {
    setLoadedViews((prev) => {
      const next = new Set(prev);
      next.add(selectedView);
      return next;
    });
  }, [selectedView]);

  const onServiceChange = (view: string) => {
    if (view === 'pamir') {
      navigate(lastPamirPathRef.current || '/');
    } else {
      navigate(`/service/${view}`);
    }
  };

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type !== 'IFRAME_NAV') return;

      const { view, path } = e.data;
      navigate(`/service/${view}${path}`, { replace: false });
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [navigate]);

  useEffect(() => {
    if (selectedView === 'pamir') {
      lastPamirPathRef.current = location.pathname + location.search;
      return;
    }

    const path = location.pathname.split(`/service/${selectedView}`)[1] || '/';

    const iframe = document.querySelector(
      `iframe[data-view="${selectedView}"]`,
    ) as HTMLIFrameElement | null;

    iframe?.contentWindow?.postMessage(
      { type: 'NAVIGATE', path },
      window.location.origin,
    );
  }, [location.pathname, location.search, selectedView]);

  useHotkeys([
    [
      'ctrl+shift+I',
      () =>
        notifications.show({
          message: `SENTRY DSN: ${sentryDsn || 'выключен'}, BUILD_ENV: ${__BUILD_ENV__}`,
        }),
    ],
    [
      'ctrl+shift+e',
      () => {
        throw new Error('Manual throw');
      },
    ],
  ]);

  const privateRoute = !currentRoute?.public;
  // Reauthentication belongs to the modal; keep the page and its drafts mounted.
  const preparing =
    privateRoute &&
    !reauthenticating &&
    [LoginStatus.preparing, LoginStatus.preparationError].includes(loginStatus);
  const loading =
    privateRoute &&
    [LoginStatus.pending, LoginStatus.loggingOut].includes(loginStatus);

  const authenticationRedirect =
    privateRoute && loginStatus === LoginStatus.unauthorized
      ? `${LOGIN}?next=${encodeURIComponent(location.pathname + location.search)}`
      : privateRoute && loginStatus === LoginStatus.passwordExpired
        ? CHANGE_PASSWORD
        : null;

  return (
    <>
      {authenticationRedirect ? (
        <Navigate to={authenticationRedirect} replace />
      ) : preparing ? (
        <ApplicationPreparation
          fullScreen
          onRetry={() => void prepareApplication(queryClient)}
          onLogout={logout}
        />
      ) : loading ? (
        <AppLoading />
      ) : (
        <Suspense fallback={<AppLoading />}>
          <AppShell
            header={{ height: 60 }}
            navbar={
              selectedView === 'pamir' && {
                width: 300,
                breakpoint: 'sm',
                collapsed: {
                  mobile: hidden ? true : !mobileOpened,
                  desktop: hidden ? true : !desktopOpened,
                },
              }
            }
            className='!bg-[var(--mantine-color-gray-0)] dark:!bg-dark-6'
          >
            <AppShell.Header hidden={noHeader}>
              <Group
                justify='space-between'
                align='center'
                h='100%'
                px='md'
                wrap='nowrap'
                pos='relative'
              >
                <Group h='100%' wrap='nowrap'>
                  {selectedView === 'pamir' && (
                    <>
                      <Burger
                        opened={mobileOpened}
                        onClick={toggleMobile}
                        hidden={hidden}
                        hiddenFrom='sm'
                        size='sm'
                        opacity={0.5}
                      />
                      <Burger
                        opened={desktopOpened}
                        onClick={toggleDesktop}
                        hidden={hidden}
                        visibleFrom='sm'
                        size='sm'
                        opacity={0.5}
                      />
                    </>
                  )}

                  <Title
                    order={2}
                    c='blue.6'
                    renderRoot={(props) => <Link to={HOME} {...props} />}
                    td='none'
                    className={cs('text-xl font-semibold', {
                      'pointer-events-none': !isAuth,
                    })}
                  >
                    PAMIR
                  </Title>

                  <ServiceSelector
                    value={selectedView}
                    onChange={onServiceChange}
                  />
                </Group>

                {selectedView === 'pamir' && isAuth && (
                  <Box
                    pos='absolute'
                    left={0}
                    right={0}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <Box style={{ pointerEvents: 'auto' }}>
                      <AppSearch navigation={navigation} />
                    </Box>
                  </Box>
                )}

                {selectedView === 'pamir' && isAuth && (
                  <Flex gap='xs' align='center'>
                    <NotificationsLog />
                    <Divider orientation='vertical' my='xs' />
                    <HINotifications />
                    <ExternalLinks />
                  </Flex>
                )}
              </Group>
            </AppShell.Header>

            {selectedView === 'pamir' && (
              <Navigation
                currentRoute={currentRoute?.to}
                error={navigationError}
                isFetching={isNavigationFetching}
                navigation={navigation}
              />
            )}

            <AppShell.Main className='!h-[100dvh]'>
              <Box
                className='h-full overflow-hidden'
                style={{
                  display: selectedView === 'pamir' ? 'block' : 'none',
                  position: selectedView === 'pamir' ? 'relative' : 'absolute',
                }}
              >
                <ContextMenu />
                <Stack gap='xs' className='h-full' p='md'>
                  <DynamicBreadcrumbs />
                  <Outlet />
                </Stack>
              </Box>

              {Array.from(loadedViews)
                .filter((view) => view !== 'pamir')
                .map((view) => (
                  <Box
                    key={view}
                    component='iframe'
                    data-view={view}
                    style={{
                      border: 0,
                      display: selectedView === view ? 'block' : 'none',
                      position: selectedView === view ? 'relative' : 'absolute',
                    }}
                    w='100%'
                    h='100%'
                    src={`${window.location.origin}/${view}`}
                  />
                ))}
            </AppShell.Main>
          </AppShell>
        </Suspense>
      )}
      <GlobalDialogs />
    </>
  );
});

export default App;
